import {
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  Button,
} from "@patternfly/react-core";
import { HelpIcon, SyncAltIcon } from "@patternfly/react-icons";
import { useState, useEffect } from "react";
import { useAdminClient } from "../../admin-client";
import { useRealm } from "../../context/realm-context/RealmContext";

import "./help-header.css";

export const HelpHeader = () => {
  const [open, setOpen] = useState(false);
  const [roleRequests, setRoleRequests] = useState<any[]>([]);
  const { adminClient } = useAdminClient();
  const { realm } = useRealm();

  const getTimeAgo = (timestamp: string) => {
    const now = new Date().getTime();
    const requestTime = new Date(timestamp).getTime();
    const diff = now - requestTime;

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}hr ago`;
    if (minutes > 0) return `${minutes}min ago`;
    return "just now";
  };

  const fetchRoleRequests = async () => {
    if (!realm) return;

    try {
      const users = await adminClient.users.find({
        realm,
        max: 1000, // prevent pagination issue
      });

      const allRequests = users.flatMap((user) => {
        const userRaw = user?.attributes?.pendingRoleRequests?.[0] || "[]";
        let userRequests: any[] = [];

        try {
          userRequests = JSON.parse(userRaw);
        } catch (e) {
          return [];
        }

        return userRequests.map((r: any) => ({
          ...r,
          userId: user.id,
          username: r.username || user.username,
        }));
      });

      const formatted = allRequests.map((r: any) => {
        const username = r.username || "-";
        const roleType = r.roleType === "client" ? "client" : "realm";
        const url = `${window.location.origin}/admin/master/console/#/${realm}/users/${r.userId}/role-mapping`;
        const timeAgo = getTimeAgo(r.requestedAt);

        return {
          username,
          roleType,
          url,
          timeAgo,
          raw: r,
        };
      });

      setRoleRequests(formatted);
    } catch (err) {
      console.error("Error reading pendingRoleRequests", err);
      setRoleRequests([]);
    }
  };

  useEffect(() => {
    if (realm) {
      fetchRoleRequests();
    }
  }, [realm]);

  const dropdownItems = [
    <DropdownItem key="role-requests" id="role-requests">
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong>Role Requests:</strong>
          <Button
            variant="link"
            size="sm"
            onClick={fetchRoleRequests}
            icon={<SyncAltIcon />}
            style={{ padding: "0", fontSize: "12px" }}
          >
            Refresh
          </Button>
        </div>

        {roleRequests.length > 0 ? (
          roleRequests.map((request, index) => (
            <div key={index} style={{ fontSize: "12px", marginTop: "4px" }}>
              User <b>{request.username}</b> is requesting{" "}
              <b>{request.roleType}</b> role{" "}
              <a href={request.url} style={{ color: "#0066cc" }}>
                click here
              </a>{" "}
              <span style={{ color: "#666" }}>({request.timeAgo})</span>
            </div>
          ))
        ) : (
          <div
            style={{
              fontSize: "12px",
              marginTop: "4px",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            No recent requests
          </div>
        )}
      </div>
    </DropdownItem>,
  ];

  return (
    <Dropdown
      popperProps={{ position: "right" }}
      onOpenChange={(isOpen) => setOpen(isOpen)}
      isOpen={open}
      toggle={(ref) => (
        <MenuToggle
          ref={ref}
          variant="plain"
          onClick={() => setOpen(!open)}
          aria-label="Help"
          id="help"
        >
          <HelpIcon />
        </MenuToggle>
      )}
    >
      <DropdownList>{dropdownItems}</DropdownList>
    </Dropdown>
  );
};